import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { Auth } from "aws-amplify"
import React, { useEffect, useState } from "react"
import Link from "next/link"
import * as styles from "./SupplierSignupPaymentCheckout.module.scss"
import fetchLambda from "@data/fetchLambda"
import lambdas from "@data/lambdas"
import Button from "@components/objects/Button/Button"
import Spinner from "@components/objects/Spinner/Spinner"

const { SEND_EMAIL, COGNITO_REQUEST, STRIPE_PAY } = lambdas[process.env.NEXT_PUBLIC_ENV]
const { NEXT_PUBLIC_DOMAIN } = process.env

const SupplierSignupPaymentCheckout = (props) => {
  const stripe = useStripe()
  const elements = useElements()

  const [inputs, setInputs] = useState({
    termsAndConditions: false,
  })
  const [paymentProcessing, setPaymentProcessing] = useState(false)

  const handleChange = (event) => {
    event.persist()
    const { target } = event
    const value = target.type === "checkbox" ? target.checked : target.value
    const { name } = target
    setInputs((inputs) => ({
      ...inputs,
      [name]: value,
    }))
  }

  useEffect(() => {
    setInputs((inputs) => ({
      ...inputs,
      eu_vat_id: null,
    }))
  }, [props.currency])

  const submit = async (event) => {
    event.preventDefault()
    setPaymentProcessing(true)

    const currentSession = await Auth.currentSession()
    const session = currentSession.getIdToken().getJwtToken()

    if (props.price.id !== "COMMISSION_ONLY") {
      const cardElement = elements.getElement(CardElement)
      const result = await stripe.createToken(cardElement, {
        name: inputs.name,
        address_line1: inputs.address_line1,
        address_line2: inputs.address_line2,
        address_city: inputs.address_city,
        address_state: inputs.address_state,
        address_zip: inputs.address_zip,
        address_country: inputs.address_country,
      })

      if (result.error) {
        console.log(result.error)
        alert(
          "There has been an error processing your payment, please verify that the payment details are correct. If it persists please contact us at supplier@houseofpartyplanning.com"
        )
        setPaymentProcessing(false)
        return
      }

      const { token } = result

      const { price } = props
      const { coupon } = props.price
      const vatID = inputs.eu_vat_id

      const stripePayResult = await fetchLambda(
        STRIPE_PAY,
        {
          token,
          session,
          price,
          coupon,
          vatID,
        },
        (response) => {
          setPaymentProcessing(false)
          if (response.message === "Customer already has subscription") {
            alert(
              "There has been an error processing your subscription request, according to our records it appears that a subscription for this email has already been processed. For more information please contact us at supplier@houseofpartyplanning.com"
            )
          } else {
            alert(
              "There has been an error processing your payment, please verify that the payment details are correct. If it persists please contact us at supplier@houseofpartyplanning.com"
            )
          }
        }
      )

      if (stripePayResult.ok) {
        await afterSubmit(session, stripePayResult)
      }
    } else {
      await afterSubmit(session)
    }
  }

  const afterSubmit = async (session, stripePayResult = null) => {
    const user = await Auth.currentAuthenticatedUser()
    await fetchLambda(COGNITO_REQUEST, {
      session,
      username: user.username,
      action: "update",
      attributes: [{ Name: "custom:plan", Value: props.price.id }],
    })
    await fetchLambda(COGNITO_REQUEST, {
      session,
      username: user.username,
      action: "remove",
      group: "SupplierPayment",
    })
    await fetchLambda(COGNITO_REQUEST, {
      session,
      username: user.username,
      action: "add",
      group: "SupplierForm",
    })
    const supplierName = user.attributes["custom:company_name"]
    const supplierEmail = user.attributes.email
    if (stripePayResult && stripePayResult.data.referral && stripePayResult.data.referral.email) {
      await fetchLambda(SEND_EMAIL, {
        data: {
          toEmail: stripePayResult.data.referral.email,
          emailTemplate: "hoppSupplierReferralTemplate",
          templateData: {
            supplierName: supplierName || supplierEmail,
            link: `${NEXT_PUBLIC_DOMAIN}/become-a-supplier/?ref=${stripePayResult.data.referral.itemId}`,
          },
        },
      })
    }

    await Auth.updateUserAttributes(user, {
      "custom:status": "form",
    }).then(() => props.authCheck())
  }

  return (
    <div className={styles.StripeForm}>
      <Spinner
        active={paymentProcessing}
        message="We are processing your subscription request, please do not close or refresh the page"
      />
      <form onSubmit={submit}>
        {props.price.id !== "COMMISSION_ONLY" && (
          <>
            <section>
              <h3>Billing address</h3>
              <div>
                <label htmlFor="name">Name</label>
                <input id="name" required name="name" onChange={handleChange} />
              </div>
              <div>
                <label htmlFor="address_line1">Address 1</label>
                <input required id="address_line1" name="address_line1" onChange={handleChange} />
              </div>
              <div>
                <label htmlFor="address_line2">Address 2</label>
                <input id="address_line2" name="address_line2" onChange={handleChange} />
              </div>
              <div>
                <label htmlFor="address_city">City</label>
                <input required id="address_city" name="address_city" onChange={handleChange} />
              </div>
              <div>
                <label htmlFor="address_state">Region</label>
                <input id="address_state" name="address_state" onChange={handleChange} />
              </div>
              <div>
                <label htmlFor="address_zip">Postcode</label>
                <input required id="address_zip" name="address_zip" onChange={handleChange} />
              </div>
              <div>
                <label htmlFor="address_country">Country</label>
                <input required id="address_country" name="address_country" onChange={handleChange} />
              </div>
            </section>

            {props.currency === "EUR" ? (
              <section>
                <h3>EU VAT Number</h3>
                <label>Add your EU VAT number to allow for the reverse charge scheme and removal of VAT</label>
                <input required id="eu_vat_id" name="eu_vat_id" onChange={handleChange} />
              </section>
            ) : null}

            <section>
              <h3>Your order</h3>
              <table>
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className={styles.plan_name}>{props.price.name}</td>
                    <td>{props.price.desc}</td>
                  </tr>
                </tbody>
              </table>
            </section>

            <section>
              <h3>Card details</h3>
              <CardElement
                options={{
                  hidePostalCode: true,
                }}
              />
            </section>
          </>
        )}

        <section>
          <div className="checkbox-wrapper termsandconditions">
            <input id="termsandconditions" required onChange={handleChange} name="termsAndConditions" type="checkbox" />
            <label htmlFor="termsandconditions">
              By registering your details and clicking SUBMIT you agree that you are a duly authorised officer or
              representative of the Supplier and you agree to be bound by the{" "}
              <Link href="/supplier-terms">
                <a>HOPP Supplier Terms and Conditions</a>
              </Link>{" "}
              and{" "}
              <Link href="/privacy-policy">
                <a>HOPP Privacy Policy</a>
              </Link>
              .
            </label>
          </div>
        </section>
        <Button className={styles.finalSubmit} text="Submit" type="submit" disabled={paymentProcessing} />
      </form>
    </div>
  )
}

export default SupplierSignupPaymentCheckout
